import isEmpty from 'lodash.isempty';
import { createSelector } from 'reselect';
import get from 'lodash.get';
import { isAfter, isSameDay } from 'date-fns';
import processEnrollmentData, { processAdditionalEnrollmentData } from './enrollmentReducerParsers';
import { GRADE } from '../../../constants/shared/GradesConstants';
import {
    FETCH_AMP_NAV_DATA,
    FETCH_ENROLLMENT_INFO,
    FETCH_ADDITIONAL_ENROLLMENT_INFO,
    SWITCH_PERIOD,
    SWITCH_PM_PERIOD,
    CLEAR_ADDITIONAL_ENROLLMENT_INFO,
    CLEAR_ENROLLMENT_INFO,
    SWITCH_GROWTH_ANALYSIS_PERIOD,
} from '../../actionTypes/shared/apiActionTypes';
import { PERIOD, TITLE } from '../../../constants/shared/CommonConstants';

const { SUMMARY } = PERIOD;
export const initialState = {
    data: {},
    isStaffConfigFetched: false,
    staffConfig: {},
    additionalData: {},
    isDataFetched: false,
    isAdditionalDataFetched: false,
    error: null,
    isDemoClass: false,
};

export const reducer = (state = initialState, action = {}) => {
    switch (action.type) {
        case SWITCH_PERIOD:
            return {
                ...state,
                data: { ...state.data, selectedPeriod: action.selectedPeriod },
            };
        case SWITCH_PM_PERIOD:
            return {
                ...state,
                data: { ...state.data, selectedPmPeriod: action.selectedPeriod },
            };
        case FETCH_ENROLLMENT_INFO.START:
            return {
                ...state,
                isDataFetched: false,
            };
        case FETCH_ADDITIONAL_ENROLLMENT_INFO.START:
            return {
                ...state,
                isAdditionalDataFetched: false,
            };
        case FETCH_ENROLLMENT_INFO.SUCCESS:
            return {
                ...state,
                isDataFetched: true,
                data: processEnrollmentData({
                    ...action.additionalResponseParams,
                    data: action.response,
                }),
            };
        case FETCH_ADDITIONAL_ENROLLMENT_INFO.SUCCESS:
            return {
                ...state,
                isAdditionalDataFetched: true,
                additionalData: processAdditionalEnrollmentData(action.response),
            };
        case FETCH_ENROLLMENT_INFO.ERROR:
            return {
                ...state,
                isDataFetched: true,
                data: {},
                error: action.error,
            };
        case FETCH_AMP_NAV_DATA.START:
            return {
                ...state,
                isStaffConfigFetched: false,
            };
        case FETCH_AMP_NAV_DATA.SUCCESS:
            return {
                ...state,
                isStaffConfigFetched: true,
                staffConfig: action.response,
            };
        case FETCH_AMP_NAV_DATA.ERROR:
            return {
                ...state,
                isStaffConfigFetched: true,
                error: action.error,
            };
        case FETCH_ADDITIONAL_ENROLLMENT_INFO.ERROR:
            return {
                ...state,
                isAdditionalDataFetched: true,
                additionalData: {},
                error: action.error,
            };
        case CLEAR_ADDITIONAL_ENROLLMENT_INFO:
            return {
                ...state,
                isAdditionalDataFetched: false,
                additionalData: {},
            };
        case CLEAR_ENROLLMENT_INFO:
            return {
                ...state,
                data: {},
            };
        case SWITCH_GROWTH_ANALYSIS_PERIOD:
            return {
                ...state,
                data: {
                    ...state.data,
                    selectedGrowthAnalysisPeriod: action.selectedGrowthAnalysisPeriod,
                },
            };
        default:
            return state;
    }
};

const getLicensingInfo = state => get(state.data, 'licensingInfo', []);

const getAdditionalLicensingInfo = state => get(state.additionalData, 'licensingInfo', []);

const getAccountName = state => get(state.data, 'accountName', '');

const getDistricts = state => get(state.data, 'districts', []);

const getSchools = state => get(state.data, 'schools', []);

const getClassesAndGroups = state => get(state.data, 'classesAndGroups', []);

const getCurrentSchoolYear = state => get(state.data, 'currentSchoolYear');

const getCurrentPeriod = state => get(state.data, 'currentPeriod');

const getSelectedPeriod = state => get(state.data, 'selectedPeriod');

const getSelectedPmPeriod = state => get(state.data, 'selectedPmPeriod');

const getDistrictId = state => get(state.data, 'chosenDistrictUid');

const getGradeToMeasureMapper = state => get(state.data, 'gradeAvailabilityInfo');

const getAdditionalGradeAvailabilityInfo = state =>
    get(state, 'additionalData.gradeAvailabilityInfo', []);

const getSelectedGrowthAnalysisPeriod = state => get(state.data, 'selectedGrowthAnalysisPeriod');

const getDualLanguageGradeAvailabilityInfo = createSelector(
    [getGradeToMeasureMapper, getAdditionalGradeAvailabilityInfo],
    (gradeAvailabilityInfo, additionalGradeAvailabilityInfo) => ({
        gradeAvailabilityInfo,
        additionalGradeAvailabilityInfo,
    }),
);

const hasAppLicenseByName = (state, appName) => getLicensingInfo(state).includes(appName);

const getDistrictName = createSelector([getDistricts, getDistrictId], (districts, uid) =>
    isEmpty(districts)
        ? ''
        : get(
              districts.find(district => district.uid === uid),
              'name',
              '',
          ),
);

const getDistrictInfo = createSelector([getDistrictId, getDistrictName], (uid, name) => ({
    uid,
    name,
}));

const getSchoolId = state => get(state.data, 'chosenSchoolUid');

const getSchoolName = createSelector([getSchools, getSchoolId], (schools, uid) =>
    isEmpty(schools)
        ? ''
        : get(
              schools.find(school => school.uid === uid),
              'name',
              '',
          ),
);

const getSchoolInfo = createSelector([getSchoolId, getSchoolName], (uid, name) => ({
    uid,
    name,
}));

const getClassOrGroupId = state => get(state.data, 'chosenClasseUid');

const getClassOrGroupName = createSelector(
    [getClassesAndGroups, getClassOrGroupId],
    (classesAndGroups, uid) =>
        isEmpty(classesAndGroups)
            ? ''
            : get(
                  classesAndGroups.find(classOrGroup => classOrGroup.uid === uid),
                  'name',
                  '',
              ),
);

const getClassOrGroupOfficialTeacher = createSelector(
    [getClassesAndGroups, getClassOrGroupId],
    (classesAndGroups, uid) => {
        const classOrGroup = classesAndGroups.find(
            classOrGroupItem => classOrGroupItem.uid === uid,
        );
        return {
            teacherName: get(classOrGroup, 'teacherName', ''),
            teacherSurname: get(classOrGroup, 'teacherSurname', ''),
        };
    },
);

const getClassOrGroupInfo = createSelector(
    [getClassOrGroupId, getClassOrGroupName],
    (uid, name) => ({
        uid,
        name,
    }),
);

const getSelectedPeriodWithoutSummary = createSelector(
    [getSelectedPeriod, getCurrentPeriod],
    (selectedPeriod, currentPeriod) =>
        selectedPeriod === SUMMARY ? currentPeriod : selectedPeriod,
);

const isEnrollmentDataExists = state => !isEmpty(state.data);

const getUserId = state => get(state.data, 'userId');

const getUserFirstName = state => get(state.data, 'userFirstName');

const getUserLastName = state => get(state.data, 'userLastName');

const isDataFetched = state => state.isDataFetched;

const isAdditionalEnrollmentDataFetched = state => state.isAdditionalDataFetched;

const isStaffConfigFetched = state => state.isStaffConfigFetched;

const getStaffConfig = state => get(state, 'staffConfig', {});

const getChosenClassStudentsByGrade = state => get(state.data, 'studentsByGrade', []);

const getStudentIds = createSelector(getChosenClassStudentsByGrade, chosenClassStudentsByGrade =>
    isEmpty(chosenClassStudentsByGrade)
        ? []
        : chosenClassStudentsByGrade.reduce(
              (studentIds, studentsByGradeObject) => [
                  ...studentIds,
                  ...studentsByGradeObject.students.map(student => student.externalId),
              ],
              [],
          ),
);

const getStudentById = (state, id) => {
    const studentsByGrade = getChosenClassStudentsByGrade(state);
    let student = {};
    studentsByGrade.forEach(studentsByGradeObject => {
        const selectedStudent = studentsByGradeObject.students.find(
            object => object.externalId === id,
        );
        if (!isEmpty(selectedStudent)) {
            student = { ...selectedStudent };
        }
    });
    return student;
};

const getGradesInCurrentClass = createSelector(
    getChosenClassStudentsByGrade,
    chosenClassStudentsByGrade =>
        !isEmpty(chosenClassStudentsByGrade)
            ? chosenClassStudentsByGrade.map(studentsByGradeObject => studentsByGradeObject.grade)
            : [],
);

const getSelectedGradeByDefault = createSelector(getGradesInCurrentClass, grades =>
    grades.length > 1 ? GRADE.ALL_GRADES : grades[0],
);

const getDefaultGradeInCurrentClass = createSelector(getGradesInCurrentClass, grades => grades[0]);

const emptyWindowDates = {
    startDate: null,
    endDate: null,
};
const getWindowDates = state =>
    get(state.data, 'chosenSchoolWindowDates', {
        BOY: emptyWindowDates,
        MOY: emptyWindowDates,
        EOY: emptyWindowDates,
    });

const getWindowDatesForSelectedPeriod = createSelector(
    [getWindowDates, getSelectedPeriod],
    (windowDates, selectedPeriod) => get(windowDates, selectedPeriod, {}),
);

const getWindowDatesForCurrentPeriod = createSelector(
    [getWindowDates, getCurrentPeriod],
    (windowDates, currentPeriod) => get(windowDates, currentPeriod, {}),
);

const isAfterEoyCurrentPeriod = createSelector([getWindowDates], data => {
    const today = new Date();
    const EOYEndsDate = new Date(data.EOY.endDate);

    if (isSameDay(today, EOYEndsDate)) {
        return false;
    }

    return isAfter(today, EOYEndsDate);
});

const getSummerPeriodLabel = state => get(state.data, 'summerPeriodLabel', TITLE.SUMMER_TITLE);

const getHomeConnectAccountInfo = createSelector(
    [
        getAccountName,
        getSchoolName,
        getClassOrGroupOfficialTeacher,
        getCurrentSchoolYear,
        getSelectedPeriod,
        getCurrentPeriod,
        getWindowDates,
        getSummerPeriodLabel,
    ],
    (
        accountName,
        name,
        officialTeacher,
        currentSchoolYear,
        selectedPeriod,
        currentPeriod,
        windowDates,
        summerPeriodLabel,
    ) => {
        const selectedPeriodLabel =
            selectedPeriod === PERIOD.SUMMER ? summerPeriodLabel : selectedPeriod;
        const currentPeriodLabel =
            currentPeriod === PERIOD.SUMMER ? summerPeriodLabel : currentPeriod;
        return {
            accountName,
            schoolName: name,
            officialTeacher,
            currentSchoolYear,
            period: selectedPeriod === SUMMARY ? currentPeriodLabel : selectedPeriodLabel,
            currentPeriod: currentPeriodLabel,
            windowDates,
        };
    },
);

const isDemoClass = state => get(state.data, 'isDemoClasse', false);

const getLrsTrackingInfo = createSelector(
    [getAccountName, getDistrictId, getSchoolId, getClassOrGroupId, isDemoClass],
    (accountName, districtId, schoolId, classGroupId, isDemo) => ({
        'district-id': districtId,
        'school-id': schoolId,
        'class-id': classGroupId,
        'is-demo-mode': isDemo,
    }),
);

const hasTKInCurrentClass = createSelector(
    [getChosenClassStudentsByGrade],
    chosenClassStudentsByGrade =>
        chosenClassStudentsByGrade.some(grade => grade.students.some(student => student.tk)),
);

const getDualLanguageLicensingInfo = createSelector(
    [getLicensingInfo, getAdditionalLicensingInfo],
    (licensingInfo, additionalLicensingInfo) => licensingInfo.concat(additionalLicensingInfo),
);

const isChosenSchoolWindowDatesStrictlyEnforced = state =>
    get(state.data, 'isChosenSchoolWindowDatesStrictlyEnforced', false);

const getGrowthAnalysisPeriodObject = createSelector(
    [getSelectedGrowthAnalysisPeriod],
    growthAnalysisPeriod => ({
        startPeriod: growthAnalysisPeriod?.split('_')[0],
        endPeriod: growthAnalysisPeriod?.split('_')[1],
    }),
);

const getToyPeriods = createSelector([getWindowDates], windowDates => Object.keys(windowDates));

export const selectors = {
    getCurrentSchoolYear,
    getCurrentPeriod,
    getSelectedPeriod,
    getSelectedPmPeriod,
    getDistricts,
    getDistrictInfo,
    getDistrictId,
    getSchools,
    getSchoolId,
    getSchoolInfo,
    getClassesAndGroups,
    getClassOrGroupId,
    getClassOrGroupInfo,
    getGradesInCurrentClass,
    getSelectedGradeByDefault,
    getDefaultGradeInCurrentClass,
    getSelectedPeriodWithoutSummary,
    getGradeToMeasureMapper,
    getDualLanguageGradeAvailabilityInfo,
    isEnrollmentDataExists,
    getUserId,
    getUserFirstName,
    getUserLastName,
    isDataFetched,
    isStaffConfigFetched,
    getStaffConfig,
    isAdditionalEnrollmentDataFetched,
    getStudentIds,
    getChosenClassStudentsByGrade,
    getStudentById,
    getLicensingInfo,
    getDualLanguageLicensingInfo,
    hasAppLicenseByName,
    getWindowDates,
    getWindowDatesForSelectedPeriod,
    getHomeConnectAccountInfo,
    getLrsTrackingInfo,
    hasTKInCurrentClass,
    getWindowDatesForCurrentPeriod,
    isDemoClass,
    isChosenSchoolWindowDatesStrictlyEnforced,
    getClassOrGroupOfficialTeacher,
    isAfterEoyCurrentPeriod,
    getGrowthAnalysisPeriodObject,
    getSelectedGrowthAnalysisPeriod,
    getToyPeriods,
    getSummerPeriodLabel,
};
